import { ThemeProvider, THEME } from '@vodacomsa/ns-react-components';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { useRecoilValue, useRecoilState } from 'recoil';
import { FormSubmitHelper, SupportPopup, ErrorModalSection } from '../../../components';
import { ValidationSchema } from './ValidationSchema';
import { FormContainer, FormField } from '../../../constants';
import { mapValues, stripEmpty } from '../../../helpers';
import { findFirstValidationError } from '../../../helpers/formik';
import { useIsMobile, authDetail } from '../../../recoil';
import { useTealium } from '../../../tealium/useTealium';
import styles from './IdentityVerification.module.css';
import { FormBuilder } from '../../../components/FormBuilder';
import { formFields } from './identityVerificationConstant';
import { userVerification } from '../../../api/vodaLend/sfUserVerification';
import { useModal } from '../../../components/modalController/useModal';
import { useLoader } from '../../../components/Loader/useLoader';
import { ApplicationRoutes } from '../../../Router';

export const IdentityVerification = () => {
  const { submitState, setSubmitState, backClick, submissionStates, nextClick } = useOutletContext();
  const formRef = React.useRef();
  const location = useLocation();
  const isMobile = useRecoilValue(useIsMobile);
  const { track, events } = useTealium();
  const [openModal, closeModal] = useModal();
  const [isNextAllowed, setIsNextAllowed] = useState(true);
  const [userVerificationResponse, setUserVerificationResponse] = useState({});
  const [openLoader, closeLoader] = useLoader();
  const [authDetails, setAuthDetails] = useRecoilState(authDetail);

  useEffect(() => {
    if (authDetails?.verification?.isVerified) {
      window.location.pathname = ApplicationRoutes.personalDetails;
    }
  }, [authDetails]);

  const formDetails = {
    idType: 'id',
    passportNumber: '',
    idNumber: ''
  };

  let formTouched = false;

  const handleFormFocus = () => {
    if (formTouched) {
      return;
    }

    formTouched = true;
    track(events.identityVerification.formStart());
  };

  const formatFormData = (formData) => mapValues(stripEmpty(formData));

  const formSubmit = async (formValues) => {
    openLoader();
    const identityType = formValues.idType === 'id' ? 'SA ID Number' : 'Passport';
    track(events.identityVerification.formEnd(identityType));
    const submission = formatFormData(formValues);
    submission.idNumber = (submission.idType === 'pp'|| submission.idType === 'sapp') ? submission.passportNumber : submission.idNumber;
    const verificationResponse = await userVerification(submission);
    setUserVerificationResponse(verificationResponse);

    if (verificationResponse.data.error) {
      setIsNextAllowed(false);
      setSubmitState(submissionStates.submitError);
    } else {
      setAuthDetails((prevState) => ({
        ...prevState,
        verification: {
          isVerified: true
        }
      }
      ));
      setIsNextAllowed(true);
      setSubmitState(submissionStates.submitSuccess);
    }
    closeLoader();
  };

  const handleFormikTouchedErrors = (touched, errors) => {
    const errorMessage = findFirstValidationError(errors, touched);
    if (errorMessage) {
      track(events.error('form', location.pathname, errorMessage));
    }
  };

  useEffect(() => {
    track(events.identityVerification.pageView());
  }, []);

  const closeErrorModal = () => {
    setSubmitState(submissionStates.default);
    closeModal();
  };

  useEffect(() => {

    if (!isNextAllowed) {
      setTimeout(() => {
        openModal({
          title: 'Error',
          children: <ErrorModalSection
            traceId={userVerificationResponse?.traceId}
            error={userVerificationResponse?.data?.error}
            onCloseErrorModal={closeErrorModal}
          />,
          type: 'error',
          onCloseModal: closeErrorModal
        });
        return;
      }, 0);
    }

  }, [isNextAllowed, userVerificationResponse]);
  useEffect(() => {
    if (submitState === submissionStates.submitting && isNextAllowed) {
      setSubmitState(submissionStates.submitSuccess);
    }
  }, [submitState, isNextAllowed]);
  return (
    <div className={styles.container}>
      <ThemeProvider theme={THEME.revamp}>
        <Formik
          initialValues={formDetails}
          onSubmit={formSubmit}
          validate={(values) => {
            try {
              ValidationSchema.validateSync(values, { abortEarly: false });
              return {};
            } catch (error) {
              return error.inner.reduce((acc, err) => {
                acc[err.path] = err.message;
                return acc;
              }, {});
            }
          }}
          validateOnBlur={true}
          validateOnMount={true}
          innerRef={formRef}
        >
          {({ values, errors, touched }) => {
            handleFormikTouchedErrors(touched, errors);
            return (
              <Form style={FormContainer} onFocus={handleFormFocus}>
                <FormSubmitHelper submitState={submitState} setSubmitState={setSubmitState} />
                <FormBuilder
                  fields={formFields}
                  title="Approved business representative details"
                  subtitle={'Please provide approved business representative information.'}
                  isMobile={isMobile}
                  values={values}
                  FormField={FormField}
                  errors={errors}
                  submitState={submitState}
                  setSubmitState={setSubmitState}
                  backClick={backClick}
                  submissionStates={submissionStates}
                  nextClick={nextClick}
                  touched={touched}
                />
              </Form>
            );
          }}
        </Formik>
      </ThemeProvider>
      <SupportPopup />
    </div>
  );
};
